import React, { useRef, useEffect } from 'react';
import { Send, Loader2 } from 'lucide-react';

interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const LINE_HEIGHT = 24;
const MAX_LINES = 6;
const MAX_HEIGHT = LINE_HEIGHT * MAX_LINES;

/**
 * Composer - Barre de texte fixe en bas (style ChatGPT)
 */
export const Composer: React.FC<ComposerProps> = ({
  value,
  onChange,
  onSend,
  isLoading = false,
  disabled = false,
  placeholder = 'Ask me anything...',
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(scrollHeight, MAX_HEIGHT);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
  }, [value]);

  // Forcer la visibilité - CRITIQUE pour que le Composer soit toujours visible
  useEffect(() => {
    console.log('🔍 Composer useEffect - forcing visibility');
    if (containerRef.current) {
      const element = containerRef.current;
      console.log('✅ Composer container found, forcing styles');
      // Forcer les styles avec !important
      element.style.setProperty('display', 'block', 'important');
      element.style.setProperty('visibility', 'visible', 'important');
      element.style.setProperty('opacity', '1', 'important');
      element.style.setProperty('z-index', '10001', 'important');
      element.style.setProperty('position', 'relative', 'important');
      
      // Vérifier périodiquement que les styles sont toujours appliqués
      const interval = setInterval(() => {
        const computed = window.getComputedStyle(element);
        if (computed.display === 'none' || computed.visibility === 'hidden' || computed.opacity === '0') {
          console.warn('⚠️ Composer was hidden! Forcing visibility...');
          element.style.setProperty('display', 'block', 'important');
          element.style.setProperty('visibility', 'visible', 'important');
          element.style.setProperty('opacity', '1', 'important');
        }
      }, 1000);
      
      return () => clearInterval(interval);
    } else {
      console.error('❌ Composer containerRef is null!');
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !isLoading && !disabled) {
        onSend();
      }
    }
  };

  const handleSend = () => {
    if (value.trim() && !isLoading && !disabled) {
      onSend();
    }
  };

  return (
    <div 
      ref={containerRef}
      className="w-full px-4 py-4 bg-[rgb(var(--bg))]"
      style={{
        paddingBottom: `calc(1rem + env(safe-area-inset-bottom, 0px))`,
        position: 'relative',
        zIndex: 10001,
        display: 'block',
        visibility: 'visible',
        opacity: 1,
        minHeight: '100px',
        width: '100%',
      }}
    >
      <div className="relative">
        {/* Textarea avec padding pour le bouton */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          rows={1}
          className="w-full px-4 py-3 pr-14 text-base bg-[rgb(var(--elevated))] border border-[rgb(var(--border))] rounded-2xl text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted-2))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--primary))]/40 focus:border-transparent disabled:opacity-50 resize-none transition-all"
          style={{
            minHeight: '52px',
            maxHeight: `${MAX_HEIGHT}px`,
            display: 'block',
            visibility: 'visible',
            opacity: disabled || isLoading ? 0.5 : 1,
          }}
        />
        
        {/* Bouton Send - Centré verticalement */}
        <button
          onClick={handleSend}
          disabled={!value.trim() || isLoading || disabled}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-[rgb(var(--primary))] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed text-[rgb(var(--primary-fg))] rounded-xl transition-all flex items-center justify-center"
          aria-label="Send message"
          style={{
            minHeight: '36px',
            minWidth: '36px',
          }}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      </div>
      
      {/* Hint text */}
      <p className="text-xs text-[rgb(var(--muted))] mt-2 text-center px-4">
        Press Enter to send, Shift+Enter for new line
      </p>
    </div>
  );
};
